import React, {useCallback, useEffect, useState} from 'react'
import CanvasDesign from "./CanvasDesign";
import {CanvasSidebar} from "./CanvasSidebar";
import {CanvasTemplateObjectAddActions} from "./CanvasActions/CanvasTemplateObjectAddActions";
import {useCanvasDispatch, useCanvasState} from "../../context/canvas/CanvasIndex";
import {useNavigate} from "react-router-dom";
import {CanvasActionRemoveModal} from "./CanvasActions/CanvasActionRemoveModal";
import {resizeObject} from "../../context/canvas/CanvasActions";

export const Canvas = ({companyId, projectId, id}) => {
  const canvasState = useCanvasState()
  const canvasDispatch = useCanvasDispatch()
  const navigate = useNavigate()
  const [showRemoveModal, setShowRemoveModal] = useState(false);

  const hasUnsavedChanges = useCallback(() => {
    let current = canvasState.objects.map((object) => ({
      ...object,
      visible: null,
      selected: null
    }))
    let old = canvasState.objectsBeforeSave.map((object) => ({
      ...object,
      visible: null,
      selected: null
    }))
    return JSON.stringify(current) !== JSON.stringify(old)
  }, [canvasState])

  // Handle keyboard interactions (delete and arrow keys)
  const handleKeyDown = useCallback((e) => {
    // Only proceed if an object is selected
    if (canvasState.selectedObject && Object.keys(canvasState.selectedObject).length > 0) {
      // Check if the event target is an input field or textarea
      const target = e.target;
      const tagName = target.tagName.toLowerCase();
      
      // Only handle keyboard events if not editing text
      if (tagName !== 'input' && tagName !== 'textarea' && !target.isContentEditable) {
        // Handle Delete key
        if (e.key === 'Delete') {
          e.preventDefault();
          setShowRemoveModal(true);
          return;
        }
        
        // Handle arrow keys for moving the object
        const selectedObj = canvasState.objects.find(obj => obj.id === canvasState.selectedObject.id);
        if (selectedObj) {
          // Get current position and dimensions
          const currentX = selectedObj.object.data.origin.x;
          const currentY = selectedObj.object.data.origin.y;
          const width = selectedObj.object.data.size.x;
          const height = selectedObj.object.data.size.y;
          
          // Calculate delta based on arrow key
          let xDelta = 0;
          let yDelta = 0;
          
          switch (e.key) {
            case 'ArrowUp':
              yDelta = -1;
              break;
            case 'ArrowDown':
              yDelta = 1;
              break;
            case 'ArrowLeft':
              xDelta = -1;
              break;
            case 'ArrowRight':
              xDelta = 1;
              break;
            default:
              return; // Not an arrow key, do nothing
          }
          
          // If shift key is pressed, move 10 pixels at a time
          if (e.shiftKey) {
            xDelta *= 10;
            yDelta *= 10;
          }
          
          // Only proceed if we're actually moving
          if (xDelta !== 0 || yDelta !== 0) {
            e.preventDefault();
            
            // Move the object using the existing resizeObject function
            // This function updates position while maintaining width/height
            resizeObject(
              canvasDispatch, 
              selectedObj.id, 
              currentX + xDelta, 
              currentY + yDelta, 
              width, 
              height
            );
          }
        }
      }
    }
  }, [canvasState.selectedObject, canvasState.objects, canvasDispatch]);

  useEffect(() => {
    // Add event listener for keydown
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    const handleUnload = (e) => {
      e.preventDefault();
      if (hasUnsavedChanges() && canvasState.template.render_status === 'DRAFT') {
        const mensaje = "Do you want to close this tab? There are unsaved changes";
        e.returnValue = mensaje;
        return mensaje;
      }
    }
    window.addEventListener("beforeunload", handleUnload);
    return () => {
      window.removeEventListener("beforeunload", handleUnload);
    }
  }, [canvasState.objects, canvasState.objectsBeforeSave, hasUnsavedChanges]);

  useEffect(() => {
    const handlePopstate = (e) => {
      e.preventDefault()
      if (hasUnsavedChanges() && canvasState.template.render_status === 'DRAFT') {
        if (window.confirm('Do you want to close this tab? There are unsaved changes')) {
          navigate(`/admin/companies/${companyId}/projects/${projectId}/templates/${id}`)
        }
      } else {
        navigate(`/admin/companies/${companyId}/projects/${projectId}/templates/${id}`)
      }
    };

    window.history.pushState(null, null, window.location.href);
    window.onpopstate = handlePopstate;

    return () => {
      window.onpopstate = null;
    };
  }, [canvasState.objects, canvasState.objectsBeforeSave, companyId, hasUnsavedChanges, id, navigate, projectId]);

  return (
    <>
      <div className={'canvas_box vh-100'}>
        <CanvasTemplateObjectAddActions/>
        <CanvasDesign
          companyId={companyId}
          projectId={projectId}
          id={id}
        />
        <CanvasSidebar/>

        <CanvasActionRemoveModal
          showModal={showRemoveModal}
          setShowModal={setShowRemoveModal}
        />
      </div>
    </>
  )
}
